import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Drawer,
  Empty,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  notification,
} from 'antd';
import {
  DollarOutlined,
  EditOutlined,
  InboxOutlined,
  PlusOutlined,
  ReloadOutlined,
  RocketOutlined,
  SearchOutlined,
  ToolOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import lib from '../../assets/utils/lib';
import pmApi from '../planManagement/pmApi';
import PlanBandsPanel from '../planManagement/PlanBandsPanel';
import { useAuth } from '../../assets/utils/useAuth';
import { usePermissions } from '../../assets/utils/usePermissions';
import DashboardSection from '../dashboard/components/DashboardSection';
import { TableSkeleton } from '../dashboard/components/DashboardSkeleton';
import { ADMIN_REVIEW_MODAL_CLASS } from '../dashboard/utils';
import {
  FALLBACK_SEGMENT_OPTIONS,
  PLAN_CATEGORY_META,
  canManagePlanCatalog,
  isTruthyPlanFlag,
  normalizePlanSegments,
  parsePlanCategories,
  taxonomyFlagMeta,
  taxonomySegmentOptions,
} from './planCatalogHelpers';

const { Text } = Typography;
const { TextArea } = Input;

const MODAL_PROPS = {
  rootClassName: ADMIN_REVIEW_MODAL_CLASS,
  className: `${ADMIN_REVIEW_MODAL_CLASS}__dialog`,
};

const STATUS_FILTER_OPTIONS = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
  { label: 'Archived', value: 'archived' },
];

const TYPE_FILTER_OPTIONS = [
  { label: 'SME', value: 'sme' },
  { label: 'Corporate', value: 'corporate' },
  { label: 'Retail', value: 'retail' },
  { label: 'Utility', value: 'utility' },
];

/** Archived > inactive > active — the same precedence the category chips use. */
const planStatusOf = (row) => {
  if (isTruthyPlanFlag(row?.plan_archived)) return 'archived';
  if (row?.plan_status == null || isTruthyPlanFlag(row?.plan_status)) return 'active';
  return 'inactive';
};

/**
 * Utility plans are the non-purchasable ones (facility onboarding), and so
 * have no market segment; everything else is typed by its segments, of which
 * a plan may hold several.
 */
const planTypesOf = (row) => {
  if (!isTruthyPlanFlag(row?.plan_purchasable ?? 1)) return ['utility'];
  const segments = normalizePlanSegments(row);
  return segments.length ? segments : [];
};

/**
 * Plans Catalogue (Oct 2026 interface update): one row per plan, typed by its
 * Categories chips — the former Retail / Corporate / SME price columns are
 * gone, since prices now belong to Plan + Band pairs. "Bands & Pricing" opens
 * that plan's bands in a drawer where everything is editable in place; the
 * general Bands & Pricing tab remains the master setup.
 *
 * @param pmPlans  optional plan rows from GET /v2/admin/pm/meta (the workspace
 *                 passes them) — adds priced-band counts and launch state.
 * @param onPlanBandsChanged  called after any edit in the drawer.
 */
const PlanCatalogPanel = ({ onCreatePlan, onOpenDesigner, onOpenBands, pmPlans, onPlanBandsChanged } = {}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasAnyPermission } = usePermissions();
  const canWrite = useMemo(
    () => canManagePlanCatalog(user, hasAnyPermission),
    [user, hasAnyPermission],
  );

  // Inside the Plan Management workspace, onOpenDesigner/onOpenBands switch
  // tabs in place. Outside it (the legacy Plans module tab, on its way out),
  // fall back to opening the workspace itself rather than the old plan editor.
  const openPlanDesigner = (plan) => {
    const planId = plan?.plan_id ?? plan?.id;
    if (planId == null) return;
    if (typeof onOpenDesigner === 'function') { onOpenDesigner(plan); return; }
    navigate(`/admin/plan-management?plan=${encodeURIComponent(planId)}&tab=designer`);
  };

  // Plan-level Bands & Pricing: a drawer scoped to one plan. onOpenBands (the
  // tab switch to the general setup) is kept for callers that still want it.
  const [bandsPlan, setBandsPlan] = useState(null);
  const openBands = (plan) => {
    const planId = plan?.plan_id ?? plan?.id;
    if (planId == null) return;
    setBandsPlan(plan);
  };
  const openGeneralBands = (plan) => {
    const planId = plan?.plan_id ?? plan?.id;
    if (planId == null) return;
    if (typeof onOpenBands === 'function') { onOpenBands(plan); return; }
    navigate(`/admin/plan-management?plan=${encodeURIComponent(planId)}&tab=bands`);
  };

  const pmById = useMemo(() => {
    const m = {};
    (pmPlans || []).forEach((p) => { m[Number(p.plan_id)] = p; });
    return m;
  }, [pmPlans]);

  const [taxonomy, setTaxonomy] = useState(null);
  // One list of every plan, narrowed by filters — never separate pages per segment.
  const [statusFilter, setStatusFilter] = useState(null);
  const [typeFilter, setTypeFilter] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [taxonomyLoading, setTaxonomyLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  const [editPlan, setEditPlan] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editForm] = Form.useForm();

  const [archiveTarget, setArchiveTarget] = useState(null);
  const [archiveReason, setArchiveReason] = useState('');
  const [archiveBlocking, setArchiveBlocking] = useState(null);
  const [archiving, setArchiving] = useState(false);

  const segmentOptions = useMemo(
    () => taxonomySegmentOptions(taxonomy, FALLBACK_SEGMENT_OPTIONS),
    [taxonomy],
  );

  const purchasableFlag = useMemo(
    () => taxonomyFlagMeta(taxonomy, 'plan_purchasable'),
    [taxonomy],
  );

  const loadTaxonomy = useCallback(async () => {
    setTaxonomyLoading(true);
    try {
      const parsed = lib.parseApiEnvelope(await lib.getAdminPlansTaxonomy());
      if (parsed.ok && parsed.data) setTaxonomy(parsed.data);
    } catch (_) {
      /* fallback segment options remain */
    } finally {
      setTaxonomyLoading(false);
    }
  }, []);

  const loadPlans = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = lib.parseAdminPlansList(await lib.getAdminPlansCatalog({ scope: 'all' }));
      if (!list.ok) {
        setError(list.message || 'Failed to load plans catalog');
        setRows([]);
        return;
      }
      setRows(list.plans);
    } catch (e) {
      setError(e?.message || 'Failed to load plans catalog');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTaxonomy();
  }, [loadTaxonomy]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const refreshAll = useCallback(async () => {
    await loadTaxonomy();
    await loadPlans();
  }, [loadTaxonomy, loadPlans]);

  const filteredRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (statusFilter && planStatusOf(row) !== statusFilter) return false;
      if (typeFilter && !planTypesOf(row).includes(typeFilter)) return false;
      if (!needle) return true;
      return [row?.plan_name, row?.plan_description, row?.plans_types_name, row?.plan_type_name]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(needle);
    });
  }, [rows, search, statusFilter, typeFilter]);

  const openEdit = (plan) => {
    if (!canWrite) return;
    setEditPlan(plan);
    const segments = normalizePlanSegments(plan);
    editForm.setFieldsValue({
      plan_name: plan?.plan_name || '',
      plan_segments: segments.length ? segments : ['corporate'],
      plan_purchasable: isTruthyPlanFlag(plan?.plan_purchasable ?? 1),
    });
  };

  const saveEdit = async () => {
    const values = await editForm.validateFields().catch(() => null);
    if (!values || !editPlan) return;
    const planId = editPlan.plan_id ?? editPlan.id;
    const wasPurchasable = isTruthyPlanFlag(editPlan?.plan_purchasable ?? 1);
    const nowPurchasable = Boolean(values.plan_purchasable);
    const segments = normalizePlanSegments(values.plan_segments);
    const payload = {
      plan_name: values.plan_name.trim(),
      plan_segments: segments,
    };
    // Avoid accidentally marking a purchasable plan as utility when only segments changed.
    if (nowPurchasable !== wasPurchasable) {
      payload.plan_purchasable = nowPurchasable ? 1 : 0;
    }
    setSaving(true);
    try {
      const parsed = await pmApi.updatePlanDetails(planId, payload);
      if (parsed.ok) {
        notification.success({ message: parsed.message || 'Plan updated', placement: 'topRight' });
        setEditPlan(null);
        await refreshAll();
      }
    } finally {
      setSaving(false);
    }
  };

  const openArchive = (plan) => {
    if (!canWrite) return;
    setArchiveTarget(plan);
    setArchiveReason('');
    setArchiveBlocking(null);
  };

  const runArchive = async (force = false) => {
    if (!archiveTarget) return;
    const planId = archiveTarget.plan_id ?? archiveTarget.id;
    setArchiving(true);
    try {
      const parsed = await pmApi.archivePlan(planId, { reason: archiveReason, force });
      if (parsed.ok) {
        notification.success({ message: parsed.message || 'Plan archived', placement: 'topRight' });
        setArchiveTarget(null);
        setArchiveBlocking(null);
        await refreshAll();
        return;
      }
      if (parsed.httpStatus === 409 && parsed.data?.code === 'PLAN_ARCHIVE_BLOCKED') {
        const blocking = parsed.data?.blocking ?? {};
        setArchiveBlocking({
          org_count: Number(blocking.org_count) || 0,
          policy_count: Number(blocking.policy_count) || 0,
        });
      }
    } finally {
      setArchiving(false);
    }
  };

  const unarchivePlan = (plan) => {
    if (!canWrite) return;
    const planId = plan.plan_id ?? plan.id;
    Modal.confirm({
      ...MODAL_PROPS,
      title: `Restore ${plan.plan_name || 'plan'}?`,
      content: 'The plan will reappear in the relevant sale catalogs.',
      okText: 'Un-archive',
      onOk: async () => {
        const parsed = await pmApi.unarchivePlan(planId);
        if (parsed.ok) {
          notification.success({ message: parsed.message || 'Plan restored', placement: 'topRight' });
          await refreshAll();
        }
      },
    });
  };

  const columns = useMemo(() => [
    {
      title: 'Plan',
      key: 'plan',
      render: (_, row) => (
        <Space direction="vertical" size={2}>
          <Text strong>{row?.plan_name || '—'}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {[row?.plans_types_name || row?.plan_type_name, row?.plan_id != null ? `ID ${row.plan_id}` : null]
              .filter(Boolean)
              .join(' · ')}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Categories',
      key: 'categories',
      width: 200,
      render: (_, row) => {
        const cats = parsePlanCategories(row);
        const display = cats.filter((c) => !(c === 'active' && cats.includes('inactive')));
        return (
          <Space size={[4, 4]} wrap>
            {display.map((cat) => {
              const meta = PLAN_CATEGORY_META[cat] || { label: cat, color: 'default' };
              const tag = <Tag key={cat} color={meta.color} style={{ margin: 0 }}>{meta.label}</Tag>;
              return meta.hint ? (
                <Tooltip key={cat} title={meta.hint}>{tag}</Tooltip>
              ) : tag;
            })}
          </Space>
        );
      },
    },
    {
      title: 'Purchasable',
      key: 'purchasable',
      width: 110,
      render: (_, row) => (isTruthyPlanFlag(row?.plan_purchasable)
        ? <Tag color="green">Yes</Tag>
        : <Tooltip title="Utility / facility plan — hidden from sale catalogs"><Tag color="default">Utility</Tag></Tooltip>),
    },
    ...(pmPlans ? [{
      title: 'Bands',
      key: 'bands',
      width: 170,
      render: (_, row) => {
        const pm = pmById[Number(row?.plan_id ?? row?.id)];
        if (!pm) return <Text type="secondary">—</Text>;
        const n = Number(pm.priced_bands || 0);
        return (
          <Space direction="vertical" size={2}>
            <Button type="link" size="small" style={{ padding: 0, height: 'auto' }} onClick={() => openBands(row)}>
              {n ? `${n} band${n === 1 ? '' : 's'} priced` : 'No bands priced'}
            </Button>
            {pm.launched_at ? (
              <Tooltip title={pm.changed_since_launch
                ? `Launched ${String(pm.launched_at).slice(0, 16)} — changed since; launch again to put changes on sale.`
                : `Launched ${String(pm.launched_at).slice(0, 16)}`}>
                <Tag icon={<RocketOutlined />} color={pm.changed_since_launch ? 'gold' : 'green'} style={{ margin: 0 }}>
                  {pm.changed_since_launch ? 'Changed since launch' : 'Launched'}
                </Tag>
              </Tooltip>
            ) : pm.lifecycle ? (
              <Tag style={{ margin: 0 }}>{pm.lifecycle}</Tag>
            ) : null}
          </Space>
        );
      },
    }] : []),
    {
      title: '',
      key: 'actions',
      width: 300,
      render: (_, row) => (
        <Space size="small" wrap>
          <Tooltip title="Assign categories, items, and limits">
            <Button size="small" icon={<ToolOutlined />} onClick={() => openPlanDesigner(row)}>
              Design
            </Button>
          </Tooltip>
          <Tooltip title="Bands attached to this plan, their prices, add / detach / delete">
            <Button size="small" icon={<DollarOutlined />} onClick={() => openBands(row)}>
              Bands &amp; Pricing
            </Button>
          </Tooltip>
          {canWrite ? (
            <>
              <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)}>Edit</Button>
              {isTruthyPlanFlag(row?.plan_archived) ? (
                <Button size="small" icon={<UndoOutlined />} onClick={() => unarchivePlan(row)}>Restore</Button>
              ) : (
                <Button size="small" danger icon={<InboxOutlined />} onClick={() => openArchive(row)}>Archive</Button>
              )}
            </>
          ) : null}
        </Space>
      ),
    },
  ], [canWrite, pmById, pmPlans]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <DashboardSection
      id="plans-catalog-management"
      title="Plan catalog"
      subtitle={`${filteredRows.length} plan${filteredRows.length === 1 ? '' : 's'}`}
      collapsible={false}
      variant="content"
      action={(
        <Space size="small" wrap>
          <Input
            size="small"
            placeholder="Search plans…"
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            style={{ width: 200 }}
          />
          <Button size="small" icon={<ReloadOutlined spin={loading || taxonomyLoading} />} onClick={refreshAll}>
            Refresh
          </Button>
          {canWrite && typeof onCreatePlan === 'function' ? (
            <Button type="primary" size="small" icon={<PlusOutlined />} onClick={onCreatePlan}>
              Create plan
            </Button>
          ) : null}
        </Space>
      )}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {!canWrite ? (
          <Alert
            type="info"
            showIcon
            message="Read-only catalog view"
            description="You need plans.manage or tariffs.manage to edit segments, pricing, or archive plans."
          />
        ) : null}

        <Space size="small" wrap>
          <Select
            size="small"
            allowClear
            style={{ width: 160 }}
            placeholder="Status: all"
            value={statusFilter}
            onChange={setStatusFilter}
            options={STATUS_FILTER_OPTIONS}
          />
          <Select
            size="small"
            allowClear
            style={{ width: 170 }}
            placeholder="Plan type: all"
            value={typeFilter}
            onChange={setTypeFilter}
            options={TYPE_FILTER_OPTIONS}
          />
          {statusFilter || typeFilter ? (
            <Button size="small" type="link" onClick={() => { setStatusFilter(null); setTypeFilter(null); }}>
              Clear filters
            </Button>
          ) : null}
        </Space>

        {statusFilter === 'archived' ? (
          <Alert
            type="info"
            showIcon
            message="Archived vs inactive"
            description="Archived plans were retired through the archive workflow. Inactive (legacy disabled) plans are not the same — filter by Inactive to see those."
          />
        ) : null}

        {error ? (
          <Alert type="error" showIcon message="Could not load catalog" description={error} />
        ) : null}

        {loading && !rows.length ? (
          <TableSkeleton rows={6} columns={5} />
        ) : filteredRows.length === 0 ? (
          <Empty
            description={statusFilter || typeFilter || search ? 'No plans match these filters' : 'No plans yet'}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <Table
            rowKey={(row) => String(row?.plan_id ?? row?.id)}
            columns={columns}
            dataSource={filteredRows}
            size="small"
            pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (t) => `${t} plans` }}
          />
        )}
      </Space>

      <Modal
        {...MODAL_PROPS}
        title={`Edit plan${editPlan?.plan_name ? ` — ${editPlan.plan_name}` : ''}`}
        open={Boolean(editPlan)}
        onCancel={() => setEditPlan(null)}
        onOk={saveEdit}
        confirmLoading={saving}
        okText="Save"
        destroyOnClose
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="plan_name"
            label="Plan name"
            rules={[{ required: true, message: 'Name is required' }]}
          >
            <Input placeholder="Plan name" />
          </Form.Item>
          <Form.Item
            name="plan_segments"
            label="Segments"
            rules={[{ required: true, type: 'array', min: 1, message: 'Select at least one segment' }]}
            extra="Who may buy or enroll on this plan (retail / corporate / SME)."
          >
            <Select mode="multiple" options={segmentOptions} placeholder="Select segments" />
          </Form.Item>
          <Form.Item
            name="plan_purchasable"
            label={purchasableFlag?.label || 'Purchasable'}
            valuePropName="checked"
            extra={purchasableFlag?.false_label
              ? `Turn off for ${purchasableFlag.false_label.toLowerCase()} (hidden from all sale catalogs).`
              : 'Turn off for utility / facility-onboarding plans (hidden from all sale catalogs).'}
          >
            <Switch
              checkedChildren={purchasableFlag?.label || 'Purchasable'}
              unCheckedChildren={purchasableFlag?.false_label || 'Utility'}
            />
          </Form.Item>
        </Form>
        <Alert
          type="info"
          showIcon
          message="Pricing has moved"
          description={(
            <span>
              Band prices (Individual / Family / Dependent) are set in Bands & Pricing, not here.{' '}
              <Button
                type="link"
                style={{ padding: 0, height: 'auto' }}
                onClick={() => { const p = editPlan; setEditPlan(null); openBands(p); }}
              >
                Open Bands & Pricing for this plan →
              </Button>
            </span>
          )}
        />
      </Modal>

      <Modal
        {...MODAL_PROPS}
        title={`Archive ${archiveTarget?.plan_name || 'plan'}`}
        open={Boolean(archiveTarget)}
        onCancel={() => { setArchiveTarget(null); setArchiveBlocking(null); }}
        confirmLoading={archiving}
        destroyOnClose
        footer={[
          <Button key="cancel" onClick={() => { setArchiveTarget(null); setArchiveBlocking(null); }} disabled={archiving}>
            Cancel
          </Button>,
          archiveBlocking ? (
            <Button key="force" type="primary" danger onClick={() => runArchive(true)} loading={archiving}>
              Archive anyway
            </Button>
          ) : (
            <Button key="archive" type="primary" danger onClick={() => runArchive(false)} loading={archiving}>
              Archive
            </Button>
          ),
        ]}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Text type="secondary">
            Archived plans are hidden from catalogs and block new assignments/uploads. Existing policies keep working.
          </Text>
          {archiveBlocking ? (
            <Alert
              type="warning"
              showIcon
              message="Plan is still in use"
              description={`This plan is assigned to ${archiveBlocking.org_count} organization(s) and ${archiveBlocking.policy_count} active policy(ies). Archive anyway to proceed.`}
            />
          ) : null}
          <div>
            <Text>Reason (optional)</Text>
            <TextArea
              rows={3}
              value={archiveReason}
              onChange={(e) => setArchiveReason(e.target.value)}
              placeholder="e.g. Replaced by 2026 Gold"
            />
          </div>
        </Space>
      </Modal>

      <Drawer
        open={Boolean(bandsPlan)}
        onClose={() => setBandsPlan(null)}
        width={Math.min(980, typeof window !== 'undefined' ? window.innerWidth - 48 : 980)}
        destroyOnClose
        title={(
          <Space direction="vertical" size={0}>
            <span>Bands &amp; Pricing — {bandsPlan?.plan_name || 'plan'}</span>
            <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
              Bands attached to this plan and what each costs. The general Bands &amp; Pricing
              setup (new sub-bands, renames, archive) is unchanged.
            </Text>
          </Space>
        )}
        extra={(
          <Button size="small" type="link" onClick={() => { const p = bandsPlan; setBandsPlan(null); openGeneralBands(p); }}>
            General band setup →
          </Button>
        )}
      >
        {bandsPlan ? (
          <PlanBandsPanel
            planId={bandsPlan.plan_id ?? bandsPlan.id}
            plan={bandsPlan}
            onChanged={() => { onPlanBandsChanged?.(); }}
          />
        ) : null}
      </Drawer>
    </DashboardSection>
  );
};

export default PlanCatalogPanel;
